import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import styles from './filterChip.module.scss';

export interface FilterChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  selected?: boolean;
  /**
   * 'default' — white chip, emerald when selected (category filters, quick filters);
   * 'status' — produce-state chips: emerald "Kasada" / orange "Meyveler"-style accents;
   * 'dark'   — slate-solid selected state (Kasada/Dalında form toggle).
   */
  tone?: 'default' | 'orange' | 'dark';
  /** 'sm' — the sub-category chips nested under a selected category. */
  size?: 'md' | 'sm';
  icon?: ReactNode;
}

/**
 * Pill chip recurring across search filters, category rows, status tags and form toggles.
 *
 * Takes a ref because Keşfet's category chip doubles as the subcategory menu's
 * trigger, and the menu is placed by measuring that chip's rect.
 */
export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(function FilterChip(
  { selected = false, tone = 'default', size = 'md', icon, className, children, ...rest },
  ref
) {
  const classes = [
    styles.chip,
    styles[tone],
    size === 'sm' ? styles.sm : '',
    selected ? styles.selected : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type="button" ref={ref} aria-pressed={selected} className={classes} {...rest}>
      {icon && <span className={styles.icon}>{icon}</span>}
      {children}
    </button>
  );
});
